import Vue from "vue";
import { Internationalization } from "@syncfusion/ej2-base";
import LocaleMixin from "@/mixins/locale";
import { formatDate, formatTime } from "@/helpers/dateTimeHelper";
const canceledWithoutCharge = 1;
/**
 * The day of a date as the start of that day in UTC, as the payments page saves it and the date picker shows it.
 * The date picker shows a value moved by the browser time zone and gives it back moved, so a payment saved at
 * the start of the day in the browser time zone would be shown, and saved again, as the day before.
 */
function toPaymentDay(date) {
    if (date == null)
        return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}
export default Vue.extend({
    mixins: [LocaleMixin],
    data() {
        return {
            appointmentId: null,
            subtitle: "",
            editorCustomerIds: [],
            rows: [],
            isLoaded: false,
            isSaving: false
        };
    },
    computed: {
        hasUnsavedCustomers() {
            if (!this.isLoaded)
                return false;
            const savedCustomerIds = this.rows.map(row => row.customerId);
            return (this.editorCustomerIds.length !== savedCustomerIds.length ||
                this.editorCustomerIds.some(customerId => !savedCustomerIds.includes(customerId)));
        }
    },
    methods: {
        async onShow(args) {
            this.appointmentId = args.appointmentId;
            this.subtitle = args.subtitle;
            this.editorCustomerIds = [...args.customerIds];
            this.rows = [];
            this.isLoaded = false;
            try {
                const payments = await this.$store.dispatch("calendar/getPayments", {
                    id: args.appointmentId
                });
                // The dialog was opened again meanwhile, for another appointment
                if (this.appointmentId !== args.appointmentId)
                    return;
                // The saved appointment is paid, also when its time was changed in the editor
                if (payments.length > 0) {
                    const payment = payments[0];
                    this.subtitle = `${formatDate(this.appLocale, payment.startDateTime)} ${formatTime(this.appLocale, payment.startDateTime)} - ${formatTime(this.appLocale, payment.endDateTime)} · ${payment.serviceName}`;
                }
                this.rows = payments.map(payment => ({
                    customerId: payment.customerId,
                    customerFullName: payment.customerFullName,
                    cancelType: payment.cancelType,
                    cost: payment.cost,
                    isPaid: payment.isPaid,
                    paymentDate: toPaymentDay(payment.paymentDate),
                    paymentNotes: payment.paymentNotes ?? "",
                    error: "",
                    saved: payment,
                    savedPaymentDate: toPaymentDay(payment.paymentDate)
                }));
                this.isLoaded = true;
            }
            catch (errors) {
                this.showErrorToast(errors);
                this.hide();
            }
        },
        getDetail(row) {
            const culture = new Internationalization(this.appLocale);
            const details = [culture.formatNumber(row.cost, { format: "C2" })];
            if (row.cancelType === canceledWithoutCharge) {
                details.push(this.$t("calendar.cancelType.canceledWithoutCharge"));
                details.push(this.$t("payments.appointment.notDue"));
            }
            else if (row.cancelType !== 0) {
                details.push(this.$t("calendar.cancelType.canceledWithCharge"));
            }
            return details.join(" · ");
        },
        isEditable(row) {
            // A customer canceled without charge does not pay, but a payment set by mistake can still be removed
            return row.cancelType !== canceledWithoutCharge || row.saved.isPaid;
        },
        onPaidChange(row, isPaid) {
            row.isPaid = isPaid;
            row.error = "";
            if (isPaid && row.paymentDate == null)
                row.paymentDate = toPaymentDay(new Date());
        },
        onPaymentDateChange(row, value) {
            // The date picker gives back the date it is set to moved, which is still the same day
            const paymentDate = toPaymentDay(value);
            if (paymentDate?.getTime() !== row.paymentDate?.getTime())
                row.paymentDate = paymentDate;
        },
        isChanged(row) {
            if (row.isPaid !== row.saved.isPaid)
                return true;
            if (!row.isPaid)
                return false;
            return (row.paymentDate?.getTime() !== row.savedPaymentDate?.getTime() ||
                row.paymentNotes !== (row.saved.paymentNotes ?? ""));
        },
        validate() {
            // Not getToday(), which on the days the clocks change is not the start of the day in UTC
            const today = toPaymentDay(new Date());
            for (const row of this.rows) {
                row.error = "";
                if (!row.isPaid)
                    continue;
                if (row.paymentDate == null)
                    row.error = this.$t("payments.appointment.dateRequired");
                else if (row.paymentDate.getTime() > today.getTime())
                    row.error = this.$t("payments.futureDateError");
            }
            return this.rows.every(row => row.error === "");
        },
        async save() {
            if (this.isSaving || this.appointmentId == null || !this.validate())
                return;
            const changedRows = this.rows.filter(row => this.isChanged(row));
            if (changedRows.length === 0) {
                this.hide();
                return;
            }
            this.isSaving = true;
            try {
                await this.$store.dispatch("calendar/updatePayments", {
                    id: this.appointmentId,
                    request: {
                        customers: changedRows.map(row => ({
                            customerId: row.customerId,
                            paymentDate: row.isPaid ? row.paymentDate : null,
                            paymentNotes: row.isPaid && row.paymentNotes !== "" ? row.paymentNotes : null
                        }))
                    }
                });
            }
            catch (errors) {
                this.showErrorToast(errors);
                return;
            }
            finally {
                this.isSaving = false;
            }
            // The calendar shows the new payment status when the editor is closed
            this.$store.commit("setAppointmentPaymentChanged", true);
            this.$toast.showSuccessToast(this.$tc("payments.update.toastSuccessTitle", changedRows.length), this.$tc("payments.update.toastSuccessContent", changedRows.length));
            this.hide();
        },
        hide() {
            this.$parent.$parent.hide();
        },
        showErrorToast(errors) {
            // The API errors are thrown as a list of messages, the unexpected failures as errors
            const message = Array.isArray(errors)
                ? errors.map(error => error.message).join("<br/>")
                : String(errors?.message ?? errors);
            this.$toast.showDangerToast(this.$t("shared.toastFailureTitle"), this.$t("shared.toastFailureContent", { error: message }));
        }
    },
    mounted() {
        // The content is created with the editor, so the payments are loaded each time the dialog is opened
        this.$parent.$parent.$on("show", this.onShow);
    },
    beforeDestroy() {
        this.$parent.$parent.$off("show", this.onShow);
    }
});
